import React from "react";

import { constants } from "@reactivated/constants";

import { ISafeHTML, isoSafeHTML } from "../models/nominals";

const AppSlug = constants["tsicommon.types.AppSlug"];

const TRADEMARK_SYMBOLS = "®™";
const TRADEMARK_SPLIT_RE = new RegExp(`([${TRADEMARK_SYMBOLS}])`);
const TRADEMARK_HTML_RE = new RegExp(`([${TRADEMARK_SYMBOLS}])(?![^<]*>)`, "g");

const needsSupTrademarks = (appSlug: string | null | undefined): boolean =>
    appSlug === AppSlug.STEARNS;

/**
 * Wraps ® / ™ in <sup> for Stearns only.
 *
 * Both brands store the same bare symbols; the difference is the font. TP's
 * Gotham draws ® small and raised, SF's Nunito Sans draws it full size on the
 * baseline. A font-level fix (unicode-range + size-adjust) shrinks the glyph
 * but drops it to the baseline, so <sup> is needed for both the size and the
 * raise. Applying it on TP would double-shrink an already-correct glyph.
 *
 * The lookahead skips symbols inside tags so attributes aren't corrupted.
 *
 * Takes and returns ISafeHTML so plain-text fields (IProduct.title is
 * t.string) cannot reach a dangerouslySetInnerHTML sink through here -- use
 * supTrademarksText for those.
 *
 * Layout contract: the emitted <sup> must render inside a container where
 * .rich-text is not a column flex container. Stearns sets that globally
 * (blocks/elements/_typography.scss) and compensates only under .configurator
 * via `.configurator .rich-text:has(> sup) { display: inline; }`. Callers
 * outside .configurator on Stearns will get the <sup> on its own line.
 */
export const supTrademarks = (
    html: ISafeHTML,
    appSlug: string | null | undefined,
): ISafeHTML =>
    needsSupTrademarks(appSlug)
        ? isoSafeHTML.wrap(
              isoSafeHTML
                  .unwrap(html)
                  .replace(TRADEMARK_HTML_RE, "<sup>$1</sup>"),
          )
        : html;

/**
 * Plain-text variant: returns React nodes, so t.string fields keep their
 * escaping guarantee and no .rich-text wrapper is introduced on brands that
 * don't need the transform.
 */
export const supTrademarksText = (
    text: string,
    appSlug: string | null | undefined,
): React.ReactNode =>
    !needsSupTrademarks(appSlug) ? (
        text
    ) : (
        <span>
            {text
                .split(TRADEMARK_SPLIT_RE)
                .map((part, i) =>
                    TRADEMARK_SPLIT_RE.test(part) ? (
                        <sup key={i}>{part}</sup>
                    ) : (
                        part
                    ),
                )}
        </span>
    );
